import React, { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import SEO from "../components/SEO";
import { useContactDrawer } from "../context/ContactDrawerContext";

export default function ServiceDetail() {
  const { slug } = useParams();
  const { openDrawer } = useContactDrawer();
  const [service, setService] = useState(null);
  const [allServices, setAllServices] = useState([]);
  const [gallery, setGallery] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [activeVideo, setActiveVideo] = useState(null);

  // Fetch current service details
  useEffect(() => {
    const fetchServiceData = async () => {
      setLoading(true);
      setError(null);
      try {
        // Fetch service by slug
        const res = await fetch(`https://api.dev.auxo.dk/public/by-slug/da/${slug}`);
        if (!res.ok) {
          throw new Error("Ydelsen kunne ikke findes");
        }
        const data = await res.json();
        setService(data);

        // Fetch all services for the sidebar
        const allRes = await fetch("https://api.dev.auxo.dk/public/content/services");
        if (allRes.ok) {
          const allData = await allRes.json();
          setAllServices(allData);
        }

        // Fetch matching gallery
        const galleryIdentifier = data.gallery_slug || data.slug?.da || slug;
        const galleryRes = await fetch("https://api.dev.auxo.dk/public/content/galleries");
        if (galleryRes.ok) {
          const galleries = await galleryRes.json();
          const matchingGallery = galleries.find(
            (g) => g.identifier === galleryIdentifier
          );
          setGallery(matchingGallery);
          if (matchingGallery && matchingGallery.images?.length > 0) {
            if (slug === "groen-vedligeholdelse") {
              const targetImg = matchingGallery.images.find(img => img.includes("img-3346.jpeg")) || matchingGallery.images[0];
              setActiveImage(targetImg);
              setActiveVideo(null);
            } else {
              setActiveImage(matchingGallery.images[0]);
              setActiveVideo(null);
            }
          }
        }
      } catch (err) {
        console.error("Fejl ved hentning af ydelsesdata:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchServiceData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-sand-50">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-brand-green-600 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-brand-sand-600 font-medium">Henter detaljer...</p>
        </div>
      </div>
    );
  }

  if (error || !service) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-brand-sand-50 px-6">
        <SEO title="Ydelse ikke fundet | Elmegaarden Have og Anlæg" />
        <div className="max-w-md text-center flex flex-col items-center gap-6 bg-white p-8 rounded-lg border border-brand-sand-200 shadow-md">
          <div className="w-16 h-16 bg-red-50 text-red-500 rounded-full flex items-center justify-center text-2xl">
            <i className="fa-solid fa-circle-exclamation" />
          </div>
          <h1 className="text-2xl font-bold text-brand-charcoal">Ydelsen blev ikke fundet</h1>
          <p className="text-brand-sand-600 text-sm">
            Vi kunne desværre ikke finde den ydelse, du leder efter. Den er muligvis blevet flyttet eller slettet.
          </p>
          <Link
            to="/"
            className="px-6 py-3 bg-brand-green-600 hover:bg-brand-green-500 text-white font-bold text-sm tracking-wide rounded-sm transition-colors"
          >
            Gå til forsiden
          </Link>
        </div>
      </div>
    );
  }

  const title = service.title?.da || service.title || "";
  const shortDesc = service.short_description?.da || service.short_description || "";
  const descriptionHtml = service.description?.da || service.description || "";
  const mainImage = service.image?.[0] || "";

  return (
    <div className="bg-brand-sand-50 min-h-screen pt-28 pb-20">
      <SEO
        title={`${title} | Elmegaarden Have og Anlæg`}
        description={shortDesc}
        ogImage={mainImage}
        canonical={`/vi-tilbyder/${slug}`}
      />

      {/* Hero Section */}
      <div className="relative h-[400px] md:h-[500px] w-full overflow-hidden bg-brand-green-950">
        {mainImage && (
          <img
            src={mainImage}
            alt={title}
            className="absolute inset-0 w-full h-full object-cover opacity-40"
          />
        )}
        <div className="absolute inset-0 bg-gradient-to-t from-brand-green-950 via-brand-green-950/40 to-transparent" />
        <div className="absolute inset-0 flex items-end">
          <div className="max-w-7xl mx-auto px-6 w-full pb-12 md:pb-16">
            <div className="max-w-3xl text-left">
              <Link
                to="/"
                className="inline-flex items-center gap-2 text-white hover:text-brand-green-400 text-xs font-bold tracking-widest uppercase mb-4 transition-colors"
              >
                <i className="fa-solid fa-arrow-left" /> Tilbage til forsiden
              </Link>
              <h1 className="text-3xl md:text-5xl font-extrabold text-white mb-4 tracking-tight">
                {title}
              </h1>
              <p className="text-lg md:text-xl text-white/90 font-light leading-relaxed">
                {shortDesc}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="max-w-7xl mx-auto px-6 mt-12 md:mt-16">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-12">
          {/* Left/Center: Description & Gallery */}
          <div className="lg:col-span-2 flex flex-col gap-12 text-left">
            {/* Rich Text Description */}
            <div className="flex flex-col gap-6">
              <h2 className="text-2xl md:text-3xl font-bold text-brand-green-950">
                Professionel {title.toLowerCase()}
              </h2>
              <div
                className="prose prose-brand max-w-none text-brand-charcoal-light leading-relaxed space-y-6"
                dangerouslySetInnerHTML={{ __html: descriptionHtml }}
              />

              {/* Call to Action inside Content */}
              <div className="mt-10 pt-8 border-t border-brand-sand-200 flex flex-col sm:flex-row items-center justify-between gap-6">
                <div>
                  <h3 className="text-lg font-bold text-brand-charcoal mb-1">
                    Skal vi hjælpe dig med dit næste projekt?
                  </h3>
                  <p className="text-sm text-brand-sand-600">
                    Kontakt os i dag for en uforpligtende snak og et skræddersyet tilbud.
                  </p>
                </div>
                <button
                  onClick={openDrawer}
                  className="px-6 py-3 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs tracking-wide uppercase rounded-sm transition-all duration-300 hover:shadow-md cursor-pointer whitespace-nowrap"
                >
                  Få et tilbud <i className="fa-solid fa-arrow-right ml-2" />
                </button>
              </div>
            </div>

            {/* Gallery Section */}
            {gallery && gallery.images && gallery.images.length > 0 && (
              <div className="flex flex-col gap-6">
                <div className="flex flex-col gap-2">
                  <h2 className="text-2xl font-bold text-brand-charcoal">
                    Se vores udførte arbejde
                  </h2>
                </div>

                {/* Main Active Image/Video Showcase */}
                {activeVideo ? (
                  <div className="relative aspect-video w-full overflow-hidden rounded-sm border border-brand-sand-200 mb-4 bg-black shadow-inner">
                    <video
                      key={activeVideo}
                      src={activeVideo}
                      controls
                      playsInline
                      className="w-full h-full object-contain"
                    />
                  </div>
                ) : activeImage ? (
                  <div className="relative aspect-video w-full overflow-hidden rounded-sm border border-brand-sand-200 mb-4 bg-brand-sand-100">
                    <img
                      src={activeImage}
                      alt={`${title} galleri`}
                      className="w-full h-full object-cover transition-all duration-500"
                    />
                  </div>
                ) : null}

                {/* Thumbnail Grid */}
                <div className="grid grid-cols-3 sm:grid-cols-5 gap-3">
                  {/* Video Thumbnails for Grøn Vedligeholdelse */}
                  {slug === "groen-vedligeholdelse" && [
                    {
                      url: "https://assets.auxodev.dk/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/img-5353.mp4"
                    },
                    {
                      url: "https://assets.auxodev.dk/assets/77df7d82-25aa-453c-8f7b-e2453151bb33/img-5334.mp4"
                    }
                  ].map((vid, vidIdx) => (
                    <button
                      key={`vid-${vidIdx}`}
                      onClick={() => {
                        setActiveVideo(vid.url);
                        setActiveImage(null);
                      }}
                      className={`relative aspect-square overflow-hidden rounded-sm border transition-all duration-300 cursor-pointer bg-black ${
                        activeVideo === vid.url
                          ? "border-[#7ca632] ring-2 ring-[#7ca632]/20 scale-95"
                          : "border-brand-sand-200 hover:border-brand-sand-400"
                      }`}
                    >
                      <video
                        src={`${vid.url}#t=0.1`}
                        preload="metadata"
                        playsInline
                        muted
                        className="w-full h-full object-cover opacity-60"
                      />
                      <div className="absolute inset-0 flex items-center justify-center">
                        <div className="w-8 h-8 rounded-full bg-[#7ca632] text-white flex items-center justify-center text-xs shadow-md">
                          <i className="fa-solid fa-play ml-0.5" />
                        </div>
                      </div>
                    </button>
                  ))}

                  {gallery.images.map((img, idx) => (
                    <button
                      key={idx}
                      onClick={() => {
                        setActiveImage(img);
                        setActiveVideo(null);
                      }}
                      className={`relative aspect-square overflow-hidden rounded-sm border transition-all duration-300 cursor-pointer ${
                        activeImage === img
                          ? "border-[#7ca632] ring-2 ring-[#7ca632]/20 scale-95"
                          : "border-brand-sand-200 hover:border-brand-sand-400"
                      }`}
                    >
                      <img
                        src={img}
                        alt={`${title} thumbnail ${idx + 1}`}
                        className="w-full h-full object-cover"
                      />
                    </button>
                  ))}
                </div>
              </div>
            )}
          </div>

          {/* Right: Sidebar with other services & contact info */}
          <div className="flex flex-col gap-8 text-left">
            {/* Other Services Navigation */}
            <div className="bg-white p-6 md:p-8 rounded-sm border border-brand-sand-200 shadow-sm">
              <h3 className="text-lg font-bold text-brand-charcoal mb-6 pb-3 border-b border-brand-sand-100">
                Vores ydelser
              </h3>
              <nav className="flex flex-col gap-2">
                {allServices.map((srv) => {
                  const srvTitle = srv.title?.da || srv.title || "";
                  const srvSlug = srv.slug?.da || srv.slug || "";
                  const isCurrent = srvSlug === slug;

                  return (
                    <Link
                      key={srv.id}
                      to={`/vi-tilbyder/${srvSlug}`}
                      className={`flex items-center justify-between p-3 rounded-sm text-sm font-semibold transition-all duration-300 ${
                        isCurrent
                          ? "bg-brand-green-900 text-white"
                          : "bg-brand-sand-50 text-brand-charcoal hover:bg-brand-sand-100 hover:text-brand-green-900"
                      }`}
                    >
                      <span>{srvTitle}</span>
                      <i className={`fa-solid fa-chevron-right text-xs ${isCurrent ? "text-brand-sand-300" : "text-brand-sand-400"}`} />
                    </Link>
                  );
                })}
              </nav>
            </div>

            {/* Quick Contact Info Box */}
            <div className="bg-brand-green-900 text-white p-8 rounded-sm shadow-md relative overflow-hidden">
              <div className="relative z-10 flex flex-col gap-6">
                <h3 className="text-xl font-bold tracking-tight">
                  Har du spørgsmål?
                </h3>
                <p className="text-sm text-white/80 leading-relaxed">
                  Vi står altid klar til at give professionel rådgivning og et uforpligtende tilbud på dit haveprojekt.
                </p>
                <div className="flex flex-col divide-y divide-white/20 mt-2">
                  <div className="flex items-center gap-4 py-4 first:pt-0">
                    <div className="text-white text-lg shrink-0 w-6 flex justify-center">
                      <i className="fa-solid fa-phone text-[#7ca632]" />
                    </div>
                    <div>
                      <a href="tel:+4520661517" className="font-bold hover:text-[#7ca632] transition-colors">
                        +45 20 66 15 17
                      </a>
                    </div>
                  </div>
                  <div className="flex items-center gap-4 py-4 last:pb-0">
                    <div className="text-white text-lg shrink-0 w-6 flex justify-center">
                      <i className="fa-solid fa-envelope text-[#7ca632]" />
                    </div>
                    <div>
                      <a href="mailto:rasmus@elmegaardenhaveoganlaeg.dk" className="font-bold hover:text-[#7ca632] transition-colors break-all">
                        rasmus@elmegaardenhaveoganlaeg.dk
                      </a>
                    </div>
                  </div>
                </div>
                <button
                  onClick={openDrawer}
                  className="mt-4 w-full py-3.5 bg-[#7ca632] hover:bg-[#6b9128] text-white font-bold text-xs uppercase tracking-wider rounded-sm transition-all duration-300 hover:shadow-lg cursor-pointer"
                >
                  Kontakt os nu
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
